import { registerUser } from "@/actions/user";
import SplashScreen from "@/components/SplashScreen";
import { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "@/hooks/use-auth-context";
import { useRouter, usePathname, useSearchParams } from "@/routes/hooks";

type Props = {
  children: React.ReactNode;
};

export function AuthGuard({ children }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const { user, loading, authenticated } = useAuthContext();
  const [isChecking, setIsChecking] = useState(true);

  const createQueryString = useCallback(
    (name: string, value: string) => {
      if (!params) return "";
      const newParams = new URLSearchParams(params.toString());
      newParams.set(name, value);
      return newParams.toString();
    },
    [params]
  );

  const getHasRegistered = useCallback(() => {
    return sessionStorage.getItem(`user_registered_${user?.id}`) === "true";
  }, [user?.id]);

  const setHasRegistered = useCallback(() => {
    if (user?.id) {
      sessionStorage.setItem(`user_registered_${user.id}`, "true");
    }
  }, [user?.id]);

  const checkAuthentication = useCallback(async () => {
    if (loading) return;

    if (!authenticated) {
      const signInPath = "/login";
      const returnTo = pathname;
      const queryString = createQueryString("returnTo", returnTo);
      const href = queryString ? `${signInPath}?${queryString}` : signInPath;
      router.replace(href);
      return;
    }
  }, [authenticated, loading, router, pathname, createQueryString]);

  const registerGoogleUser = useCallback(async () => {
    if (
      !loading &&
      authenticated &&
      user?.app_metadata?.provider === "google" &&
      !getHasRegistered()
    ) {
      try {
        const fullName = user.user_metadata?.full_name || "";
        const [firstName, ...lastNameParts] = fullName.split(" ");
        const lastName = lastNameParts.join(" ");

        const response = await registerUser({
          email: user.email ?? "",
          id: user.id,
          firstName,
          lastName,
          profilePictureUrl: user.user_metadata?.avatar_url,
        });

        if (response.ok) {
          setHasRegistered();
          if (response.data?.workspace) {
            router.push("/dashboard");
          }
        }
      } catch (error) {
        console.error("Error during user registration:", error);
      }
    }
    setIsChecking(false);
  }, [
    authenticated,
    loading,
    user,
    router,
    getHasRegistered,
    setHasRegistered,
  ]);

  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  useEffect(() => {
    registerGoogleUser();
  }, [registerGoogleUser]);

  if (loading || isChecking) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}

export default AuthGuard;
